import { ArrowsInIcon, ArrowsOutIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as styles from './graph-viewport.css.ts'

/**
 * A pan-and-zoom viewport for a diagram.
 *
 * Lifted out of `trust-zone-flow.tsx` so the mermaid flow and the typed graphs share one interaction
 * model. Two diagrams that pan and zoom slightly differently is worse than either, and the behaviour here
 * is fiddly enough — zoom about the centre, pointer capture, modifier-gated wheel — that a second
 * implementation would drift from this one within a week.
 *
 * Content is laid out at its natural size and transformed as a whole; the caller does not need to know
 * anything about scale.
 */

const ZOOM_FACTOR = 1.25
const MIN_SCALE = 0.1
const MAX_SCALE = 4
const KEYBOARD_PAN_STEP = 48

type Offset = { x: number; y: number }
type View = { scale: number; offset: Offset }

const INITIAL_VIEW: View = { scale: 1, offset: { x: 0, y: 0 } }

const clampScale = (scale: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))

const DRAG_THRESHOLD_PX = 4

export type GraphViewportProps = {
  /** Natural size of the content, used to fit it to the viewport. */
  contentSize: { width: number; height: number }
  ariaLabel: string
  /**
   * Rendered with the current zoom scale, so content can counter-scale against it — labels that grow with
   * the diagram never stop overlapping, however far you zoom in.
   */
  children: (scale: number) => React.ReactNode
  /**
   * Fired for a click that lands on the viewport rather than on anything interactive — used to clear a
   * selection. It lives here because the viewport owns the whole surface: the empty space between nodes,
   * the zone washes, and the padding around the diagram are all places a reader will click to mean
   * "nothing". A drag that happens to end in a click does not count.
   */
  onBackgroundClick?: () => unknown
}

export const GraphViewport: React.FC<GraphViewportProps> = ({
  contentSize,
  ariaLabel,
  children,
  onBackgroundClick,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View>(INITIAL_VIEW)
  const [isPanning, setIsPanning] = useState(false)
  const panOriginRef = useRef<{ pointer: Offset; offset: Offset } | undefined>(undefined)
  // A pan ends in a click, and clearing the selection every time someone repositions the diagram would be
  // maddening. Anything past a few pixels is a drag, not a click.
  const hasDraggedRef = useRef(false)

  // Zoom around the viewport centre so the graph does not lurch toward the top-left on every step.
  const zoomBy = useCallback((factor: number) => {
    const viewport = viewportRef.current
    setView(previous => {
      const nextScale = clampScale(previous.scale * factor)
      if (!viewport) {
        return { ...previous, scale: nextScale }
      }
      const centre = { x: viewport.clientWidth / 2, y: viewport.clientHeight / 2 }
      const ratio = nextScale / previous.scale
      return {
        scale: nextScale,
        offset: {
          x: centre.x - (centre.x - previous.offset.x) * ratio,
          y: centre.y - (centre.y - previous.offset.y) * ratio,
        },
      }
    })
  }, [])

  const resetView = useCallback(() => setView(INITIAL_VIEW), [])

  const fitToViewport = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport?.clientWidth || !viewport.clientHeight) {
      return
    }
    const scale = clampScale(
      Math.min(viewport.clientWidth / contentSize.width, viewport.clientHeight / contentSize.height)
    )
    setView({
      scale,
      offset: {
        x: (viewport.clientWidth - contentSize.width * scale) / 2,
        y: (viewport.clientHeight - contentSize.height * scale) / 2,
      },
    })
  }, [contentSize.width, contentSize.height])

  // Open fitted: a diagram wider than its viewport otherwise opens on a corner that is often empty.
  const hasFittedRef = useRef(false)
  useEffect(() => {
    if (!hasFittedRef.current) {
      hasFittedRef.current = true
      fitToViewport()
    }
  }, [fitToViewport])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // A press on anything interactive must not begin a pan — and must not capture the pointer, because
      // Chrome retargets the compatibility mouse events to the capturing element, so `click` would land
      // here instead of on the node and the selection would never fire. `[role="button"]` matters as much
      // as `button`: the graph nodes are `<g role="button">`, which `closest('button')` does not match.
      if (event.target instanceof Element && event.target.closest('button, [role="button"]')) {
        return
      }
      hasDraggedRef.current = false
      panOriginRef.current = { pointer: { x: event.clientX, y: event.clientY }, offset: view.offset }
      setIsPanning(true)
      event.currentTarget.setPointerCapture?.(event.pointerId)
    },
    [view.offset]
  )

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const panOrigin = panOriginRef.current
    if (!panOrigin) {
      return
    }
    if (Math.hypot(event.clientX - panOrigin.pointer.x, event.clientY - panOrigin.pointer.y) > DRAG_THRESHOLD_PX) {
      hasDraggedRef.current = true
    }
    setView(previous => ({
      ...previous,
      offset: {
        x: panOrigin.offset.x + (event.clientX - panOrigin.pointer.x),
        y: panOrigin.offset.y + (event.clientY - panOrigin.pointer.y),
      },
    }))
  }, [])

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!panOriginRef.current) {
      return
    }
    panOriginRef.current = undefined
    setIsPanning(false)
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }, [])

  /**
   * Only zoom when a modifier is held, so an ordinary wheel gesture still scrolls the page.
   *
   * Registered natively rather than as an `onWheel` prop: React attaches `wheel` to the root as a
   * *passive* listener, where `preventDefault()` is a no-op that logs a warning — the browser would
   * take the modifier gesture as a page zoom and the diagram would never scale. A listener on the
   * element itself can opt out of passive.
   */
  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) {
      return undefined
    }
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        return
      }
      event.preventDefault()
      zoomBy(event.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR)
    }
    viewport.addEventListener('wheel', handleWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', handleWheel)
  }, [zoomBy])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const panBy = (x: number, y: number) => {
        event.preventDefault()
        setView(previous => ({ ...previous, offset: { x: previous.offset.x + x, y: previous.offset.y + y } }))
      }
      switch (event.key) {
        case 'ArrowLeft':
          return panBy(KEYBOARD_PAN_STEP, 0)
        case 'ArrowRight':
          return panBy(-KEYBOARD_PAN_STEP, 0)
        case 'ArrowUp':
          return panBy(0, KEYBOARD_PAN_STEP)
        case 'ArrowDown':
          return panBy(0, -KEYBOARD_PAN_STEP)
        case '+':
        case '=':
          return zoomBy(ZOOM_FACTOR)
        case '-':
          return zoomBy(1 / ZOOM_FACTOR)
        case '0':
          return resetView()
        default:
          return undefined
      }
    },
    [zoomBy, resetView]
  )

  return (
    <div
      ref={viewportRef}
      className={isPanning ? styles.viewportPanning : styles.viewport}
      role="application"
      aria-label={`${ariaLabel} — drag to pan, arrow keys to move, plus and minus to zoom`}
      tabIndex={0}
      onClick={event => {
        // Nodes and the zoom controls speak for themselves; only a click on the surface means "nothing".
        if (event.target instanceof Element && event.target.closest('button, [role="button"]')) {
          return
        }
        if (!hasDraggedRef.current) {
          onBackgroundClick?.()
        }
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
    >
      <div
        className={styles.canvas}
        // No `scale()` here: the child sizes itself from the scale so the SVG re-renders as vector at
        // every zoom step. The geometry is identical — with `transform-origin: 0 0`, content drawn at
        // `size * scale` sits exactly where `scale()` would have put it.
        style={{ transform: `translate(${view.offset.x}px, ${view.offset.y}px)` }}
      >
        {children(view.scale)}
      </div>
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.controlButton}
          onClick={() => zoomBy(ZOOM_FACTOR)}
          aria-label="Zoom in"
          title="Zoom in"
        >
          <MagnifyingGlassPlusIcon size={16} />
        </button>
        <button
          type="button"
          className={styles.controlButton}
          onClick={() => zoomBy(1 / ZOOM_FACTOR)}
          aria-label="Zoom out"
          title="Zoom out"
        >
          <MagnifyingGlassMinusIcon size={16} />
        </button>
        <button
          type="button"
          className={styles.controlButton}
          onClick={fitToViewport}
          aria-label="Fit diagram to view"
          title="Fit to view"
        >
          <ArrowsInIcon size={16} />
        </button>
        <button
          type="button"
          className={styles.controlButton}
          onClick={resetView}
          aria-label="Reset to actual size"
          title="Actual size"
        >
          <ArrowsOutIcon size={16} />
        </button>
        <span className={styles.scaleReadout} aria-live="polite">
          {Math.round(view.scale * 100)}%
        </span>
      </div>
    </div>
  )
}
